/**
 * Tenants Redux Selectors
 */

import { createSelector } from '@reduxjs/toolkit'
import type { Tenant, TenantStats, TenantsState } from '../domain/types/tenant.types'

// Stats calculation moved inline since filter utils were removed
function calculateStats(tenants: Tenant[]): TenantStats {
  const active = tenants.filter((t) => t.status === 'active').length
  const pending = tenants.filter((t) => t.status === 'pending').length
  const suspended = tenants.filter((t) => t.status === 'suspended').length
  return {
    total: tenants.length,
    active,
    pending,
    suspended,
  }
}

interface RootState {
  tenants: TenantsState
}

// Base selectors
export const selectTenantsState = (state: RootState) => state.tenants
export const selectTenants = (state: RootState) => state.tenants.items
export const selectTenantsFilter = (state: RootState) => state.tenants.filter
export const selectTenantsPagination = (state: RootState) => state.tenants.pagination
export const selectTenantsLoading = (state: RootState) => state.tenants.isLoading
export const selectTenantsError = (state: RootState) => state.tenants.error
export const selectSelectedTenant = (state: RootState) => state.tenants.selectedTenant
export const selectStoredStats = (state: RootState) => state.tenants.stats
export const selectCustomerGroups = (state: RootState) => state.tenants.customerGroups
export const selectCustomerGroupsLoading = (state: RootState) => state.tenants.isCustomerGroupsLoading
export const selectCustomerGroupsError = (state: RootState) => state.tenants.customerGroupsError

// Alias for backward compatibility
export const selectAllTenants = selectTenants

// Backend now handles filtering, just return items
export const selectFilteredTenants = selectTenants

// Stats - prefer stored stats from backend if available, otherwise compute
export const selectTenantsStats = createSelector(
  [selectTenants, selectStoredStats],
  (tenants, storedStats) => {
    if (storedStats.total > 0) {
      return storedStats
    }
    return calculateStats(tenants)
  }
)

// Pagination convenience selectors
export const selectCurrentPage = createSelector(
  [selectTenantsPagination],
  (pagination) => pagination.page
)

export const selectPageSize = createSelector(
  [selectTenantsPagination],
  (pagination) => pagination.pageSize
)

export const selectTotalPages = createSelector(
  [selectTenantsPagination],
  (pagination) => pagination.pageCount
)

export const selectTotalItems = createSelector(
  [selectTenantsPagination],
  (pagination) => pagination.total
)

// Status-based selectors (from current page data)
export const selectPendingTenants = createSelector(
  selectAllTenants,
  (tenants) => tenants.filter(t => t.status === 'pending')
)

export const selectPendingTenantsCount = createSelector(
  selectPendingTenants,
  (tenants) => tenants.length
)

export const selectActiveTenants = createSelector(
  selectAllTenants,
  (tenants) => tenants.filter(t => t.status === 'active')
)

export const selectActiveTenantsCount = createSelector(
  selectActiveTenants,
  (tenants) => tenants.length
)

export const selectSuspendedTenants = createSelector(
  selectAllTenants,
  (tenants) => tenants.filter(t => t.status === 'suspended')
)

export const selectSuspendedTenantsCount = createSelector(
  selectSuspendedTenants,
  (tenants) => tenants.length
)

export const selectTenantById = createSelector(
  [selectAllTenants, (_state: RootState, tenantId: string) => tenantId],
  (tenants, tenantId) => tenants.find(t => t.id === tenantId) ?? null
)

export const selectTenantsByChannel = createSelector(
  [selectAllTenants, (_state: RootState, channel: string) => channel],
  (tenants, channel) => tenants.filter(t => t.channel === channel)
)
